import { compareTimeListIds } from '../time-list/compareIds';
import { ZoneId, ZonedDateTime } from '@js-joda/core';
import type { NewsStory } from '@thinkalpha/quant-ws-client/contracts/news.js';
import type {
    NewsPageRequest,
    NewsPageResponse,
    NewsPosition,
} from '@thinkalpha/quant-ws-client/contracts/time-list-page.js';
import { createPositionPageAdapter } from '@thinkalpha/ui-symbol-tracked-time-list';
import type { TimeListConfig, TimeListAdapter, SymbolTrackedTimeList } from '@thinkalpha/ui-symbol-tracked-time-list';

type NewsPageQuery = NewsPageRequest['query'];

export type NewsTimeList = SymbolTrackedTimeList<
    NewsStory,
    NewsPageQuery,
    NewsPageRequest,
    NewsPageResponse,
    NewsPosition
>;

export const NEWS_TIME_LIST_CONFIG: TimeListConfig = {
    pageSize: 79,
    retainedWindowRows: 300,
    overscanRows: 20,
    rowHeight: 24,
    separatorHeight: 24,
    bufferHeight: 120,
    pinnedTolerance: 0,
    liveInterval: 10_000,
    leadInterval: 10_000,
};

export function createNewsAdapter(
    zone = ZoneId.systemDefault(),
): TimeListAdapter<NewsStory, NewsPageQuery, NewsPageRequest, NewsPageResponse, NewsPosition> {
    const adapter = createPositionPageAdapter<NewsStory, NewsPageQuery, NewsPosition>({
        keyOf: (row) => row.id,
        compare: (a, b) => b.publishedAt.compareTo(a.publishedAt) || compareTimeListIds(b.id, a.id),
        cursorOf: ({ publishedAt, id }) => ({ publishedAt, id }),
        symbolsOf: (row) => row.symbols,
        calendarDateOf: (row) => ZonedDateTime.ofInstant(row.publishedAt, zone).toLocalDate(),
        declaresLeadSet: false,
    });
    return {
        ...adapter,
        toRequest(instruction) {
            const request = adapter.toRequest(instruction);
            if (request.kind === 'lead') throw new Error('News does not support future pages');
            return { ...request, symbols: [...instruction.symbols] } satisfies NewsPageRequest;
        },
    };
}
